import { useEffect, useRef, useState } from "react";
import { Form, Select, Badge } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../helpers/API/amplifyApi";
import { FolderIcon } from "../../../assets/icons";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { RichTextEditor } from "../../commonComponents";
import ProblemsDocUploader from "../../dashboard/AdminDashboard/AdminCards/Problems/ProblemsDocUploader/ProblemsDocUploader";
import SiderModal from "../../dashboard/AdminDashboard/AdminCards/Problems/SiderModal/SiderModal";
import { useUploadedFiles } from "../../Documentation/hooks/useUploadedFiles";
import { driveApi } from "../../DriveRequest";
import { useAuth } from "../../../authentication/authHelpers";
import dayjsTZ from "../../../utils/dayjs";
import { problematikat as problematikatAction } from "../../../store/actions";
import { notificationUserToAdmin } from "../../../utils/sendNotification";
import { eventTracker } from "../../../utils/eventTracker";
import { isElevatedRole, toMessagePreview } from "./utils/ticketStatus";

const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

function ComposeTicketForm({ onCreated, onCancel }) {
  const { user, userRole, employeeId } = useAuth();
  const dispatch = useDispatch();
  const { accessToken } = useSelector((state) => state.accessToken);
  const { activeEmployees } = useSelector((state) => state.employeesList);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const { hotCredentials } = useSelector((state) => state.hotCredentials);
  const { programFields } = useSelector((state) => state.programFields);

  const driveRequest = driveApi({ accessToken });
  const [form] = Form.useForm();
  const username = `${user?.given_name || ""} ${user?.family_name || ""}`.trim();
  const categories = useRef(
    programFields?.find((el) => el?.fieldName === "Kategorite e Problematikave")
  );

  const [parentFolderId, setParentFolderId] = useState("");
  const [googleDriveFolderId, setGoogleDriveFolderId] = useState("");
  const [isSiderModalVisible, setIsSiderModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [uploadHandlers, deleteHandlers] = useUploadedFiles([]);
  const [uploadedFiles, setUploadedFiles, formattedDriveFiles, onPickerSuccess] =
    uploadHandlers;
  const [, , onDelete] = deleteHandlers;

  const employeesOption = [];
  activeEmployees?.forEach((employee) => {
    if (employee.hasOwnProperty("userSub")) {
      employeesOption.push({
        key: employee.employeeId,
        value: employee.employeeId,
        label: `${employee.employeeFirstName} ${employee.employeeLastName}`,
      });
    }
  });

  useEffect(() => {
    if (!!hotCredentials?.length) {
      const parentFolder = hotCredentials.find(
        (el) => el?.credentialId === "7"
      );
      setParentFolderId(parentFolder?.credentialValue || "");
    }
  }, [hotCredentials]);

  useEffect(() => {
    if (!parentFolderId || googleDriveFolderId) return;
    const folderName = `Problematika ${dayjsTZ().format("DD/MM/YYYY h:mm:ss")}`;
    driveRequest
      .getFolderIdOrCreate({ name: folderName, parent: parentFolderId })
      .then((res) => setGoogleDriveFolderId(res))
      .catch((err) => console.log("Error creating folder: ", err));
  }, [parentFolderId]);

  const handleSubmit = () => {
    form.validateFields().then(async (formData) => {
      setSubmitting(true);
      let createdBy = username;
      let ownerEmployeeId = employeeId;

      if (formData.createdBy && isElevatedRole(userRole)) {
        const ticketEmployee = activeEmployees?.find(
          (el) => el?.employeeId === formData.createdBy
        );
        createdBy = `${ticketEmployee?.employeeFirstName || ""} ${
          ticketEmployee?.employeeLastName || ""
        }`.trim();
        ownerEmployeeId = formData.createdBy;
      }

      const toSend = {
        ticketCategory: formData.ticketCategory,
        ticketPriority: formData.ticketPriority || "Medium",
        ticketDescription: formData.ticketDescription,
        ticketStatus: "Open",
        googleDriveFolderId,
        googleDriveFiles: formattedDriveFiles,
        createdBy,
        employeeId: ownerEmployeeId,
        ticketLogs: [
          {
            activity: "U Krijua Problematika",
            author: username,
            changeDate: dayjsTZ().valueOf(),
            field: "",
            oldValue: "",
            newValue: "",
          },
        ],
        lastMessagePreview: toMessagePreview(formData.ticketDescription),
      };

      try {
        const resp = await API.post("tickets", `/tickets`, { body: toSend });
        dispatch(problematikatAction(resp));
        eventTracker("Support", "click", "Created a ticket");
        notificationUserToAdmin(
          {
            notificationTitle: "U krijua nje Problematike",
            notificationBody: `${createdBy} krijoi një problematikë në lidhje me ${resp?.ticketCategory}`,
            notificationUrl: `/problematikat?ticket=${resp?.ticketId}`,
          },
          user?.allUsers?.Items
        );
        form.resetFields();
        setUploadedFiles([]);
        onCreated?.(resp);
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    });
  };

  return (
    <div className="supportComposeForm">
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ ticketPriority: "Medium" }}
      >
        <div className="supportComposeForm__row">
          {isElevatedRole(userRole) && employeesOption.length > 0 && (
            <Form.Item
              label="Zgjidhni userin"
              name="createdBy"
              rules={[
                { required: true, message: "Kjo fushë nuk mund të jetë bosh!" },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Zgjidhni userin"
                options={employeesOption}
                classNames={{
                  popup: { root: isDarkMode ? "darkDropdown" : undefined },
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            label="Prioriteti"
            name="ticketPriority"
            rules={[
              { required: true, message: "Kjo fushë nuk mund të jetë bosh!" },
            ]}
          >
            <Select
              options={PRIORITY_OPTIONS}
              classNames={{
                popup: { root: isDarkMode ? "darkDropdown" : undefined },
              }}
            />
          </Form.Item>
          <Form.Item
            label="Kategoria"
            name="ticketCategory"
            rules={[
              { required: true, message: "Kjo fushë nuk mund të jetë bosh!" },
            ]}
          >
            <Select
              placeholder="Selektoni kategorinë"
              options={(categories.current?.fieldOptions || []).map((item) => ({
                value: item.name,
                label: item.name,
              }))}
              classNames={{
                popup: { root: isDarkMode ? "darkDropdown" : undefined },
              }}
            />
          </Form.Item>
        </div>

        <div className="supportComposeForm__docs">
          {uploadedFiles?.length !== 0 && (
            <Badge count={uploadedFiles.length} status="success">
              <MondayButton
                className="mondayButtonBlue"
                hasIcon={false}
                onClick={() => setIsSiderModalVisible(true)}
              >
                Dokumente
              </MondayButton>
            </Badge>
          )}
          <div className="supportComposeForm__uploader">
            <FolderIcon />
            <ProblemsDocUploader
              disabled={!googleDriveFolderId}
              onPickerSuccess={onPickerSuccess}
              uploadedFiles={uploadedFiles}
              folderId={googleDriveFolderId}
            />
          </div>
        </div>

        <Form.Item
          name="ticketDescription"
          rules={[
            { required: true, message: "Kjo fushë nuk mund të jetë bosh!" },
          ]}
        >
          <RichTextEditor
            placeholder="Përshkruani problematikën..."
            className="lajmerime-quill"
          />
        </Form.Item>
      </Form>

      <div className="supportComposeForm__footer">
        <MondayButton
          className="mondayButtonRed"
          hasIcon={false}
          onClick={onCancel}
        >
          Anulo
        </MondayButton>
        <MondayButton
          className="mondayButtonGreen"
          hasIcon={false}
          disabled={submitting || !googleDriveFolderId}
          onClick={handleSubmit}
        >
          Krijo ticket
        </MondayButton>
      </div>

      <SiderModal
        editData={{}}
        onDelete={onDelete}
        setEditData={() => {}}
        uploadedFiles={uploadedFiles}
        setIsFieldTouched={() => {}}
        isSiderModalVisible={isSiderModalVisible}
        setIsSiderModalVisible={setIsSiderModalVisible}
      />
    </div>
  );
}

export default ComposeTicketForm;
